import Image from 'next/image'

import { NextImage } from 'src/libs/types'
import { CONTENT_CONTAINER_STYLES, HEADING_2_STYLES } from 'src/libs/design-system'
import { COLOR_HUES, theme, twMerge } from 'src/libs/tailwind-css'
import { Benefits, PropositionSection } from 'src/libs/ui-shared'
import { Appear } from 'src/libs/react-utils'

interface FeatureGroupProps {
    title: string
    image: NextImage
    imageAlt: string
    imageBgColor: 'yellow' | 'blue' | 'purple'
    sections: FeatureSection[]
}

interface FeatureSection {
    title: string
    description: string
    benefits: string[]
    benefitsColor: 'blue' | 'purple' | 'red' | 'yellow' | 'green'
    image: NextImage
    imageAlt: string
}

export function FeatureGroup({
    title,
    image,
    imageAlt,
    imageBgColor,
    sections,
}: FeatureGroupProps) {
    const imageBgStyles = { yellow: 'bg-yellow-50', blue: 'bg-blue-100', purple: 'bg-purple-100' }[
        imageBgColor
    ]
    const imageBgHue = COLOR_HUES[imageBgColor]

    return (
        <article className="grid gap-24">
            <Appear className="grid gap-8">
                <h2 className={twMerge(HEADING_2_STYLES, 'max-w-sm mx-auto text-center')}>
                    {title}
                </h2>
                <div className={twMerge('py-4 lg:p-0 lg:bg-transparent', imageBgStyles)}>
                    <div
                        className={twMerge(
                            CONTENT_CONTAINER_STYLES,
                            imageBgStyles,
                            'lg:p-8 lg:rounded-[2.5rem]'
                        )}
                    >
                        <div
                            className="overflow-hidden rounded sm:rounded-lg lg:rounded-2xl"
                            style={{
                                boxShadow: `0px -4px 0px -4px hsla(${imageBgHue}, 56%, 30%, 0.1), 0px 4px 10px hsla(${imageBgHue}, 30%, 73%, 0.25), 0px 4px 40px hsla(${imageBgHue}, 30%, 73%, 0.1)`,
                            }}
                        >
                            <Image
                                src={image}
                                alt={imageAlt}
                                layout="responsive"
                                placeholder="blur"
                                // Sizes are smaller than actual on big retina-screens on purpose to reduce file size
                                sizes={`(min-width: ${theme.screens['3xl']}) and (min-resolution: 2x) 880px, (min-width: ${theme.screens['3xl']}) 1452px, (min-width: ${theme.screens.xl}) and (min-resolution: 2x) 60vw, (min-width: ${theme.screens.xl}) 80vw, 90vw`}
                            />
                        </div>
                    </div>
                </div>
            </Appear>
            {sections.map((section, index) => (
                <PropositionSection
                    key={section.title}
                    title={section.title}
                    description={section.description}
                    image={
                        <Image
                            src={section.image}
                            alt={section.imageAlt}
                            layout="responsive"
                            placeholder={section.image.src.endsWith('.svg') ? undefined : 'blur'}
                        />
                    }
                    imageShadow
                    swapElementsWhenVertical={index % 2 === 0}
                    headingLevel={3}
                >
                    <Benefits
                        benefits={section.benefits}
                        color={section.benefitsColor}
                        className="max-w-xs"
                    />
                </PropositionSection>
            ))}
        </article>
    )
}
